import { hectareFormat } from "@omnia/shared/utils/format";
import { FieldViewModel } from "../../../fields";
import { getSelectedStrokeStyle } from "../../../map/styles/defaults";
import { isArrayEmpty } from "../../../utils/array";
import { isArray, isUndefinedOrNull } from "../../../utils/utils";
import { getCropPatternName, getOrCreateCropStylePattern, loadImgPattern } from "../patterns";
import { CroppingLayer, shouldIgnoreCropInLabels, ShowFieldNameKey } from "./croppingLayer";
import { getTextStyles } from "@omnia/shared/map/styles/text";
import { ol_Feature, ol_style } from "@omnia/exports/openlayers";

export function getDefaultStrokeStyle() {
    return new ol_style.Stroke({
        color: "#444",
        width: 1,
        lineDash: [3, 3],
    });
}

const defaultStroke = getDefaultStrokeStyle();

type FillColor = number[] | string | CanvasPattern | CanvasGradient;

export class CroppingLayerStyle {
    public fillColorProvider: (cropping: FieldViewModel) => FillColor;
    public labelTextProvider: (cropping: FieldViewModel) => string;

    constructor(protected readonly layer: CroppingLayer) {}

    public getLayerStyleFunction(selected: boolean) {
        const { layer } = this;

        const selectedStroke = getSelectedStrokeStyle();

        return (feature: ol_Feature, resolution: number) => {
            const featureSelected = this.isSelected(feature, selected);

            const colorProvider = (opacity: number) => this.getCroppingColor(feature, featureSelected, opacity);

            const zoomFilter = layer.checkResolutionLabelFilter(resolution, 10);
            const text = zoomFilter && layer.getFeatureLabelVisibility(feature);

            return this.createStyle(featureSelected, feature, resolution, colorProvider, text, selectedStroke);
        };
    }

    private createStyle(
        selected: boolean,
        feature: ol_Feature,
        resolution: number,
        colorProvider: (opacity: number) => FillColor,
        includeText: boolean,
        selectedStroke
    ) {
        const { layer } = this;

        const data = <FieldViewModel>feature.get("data");
        if (!data) return;

        const highlight = layer.isFeatureHighlighted(feature);

        let color: FillColor;

        if (highlight === false || (selected && layer.showSelectedFillColor === false)) {
            color = null;
        } else {
            const opacity = this.getFillColorOpacity(selected);
            color = colorProvider(opacity);
        }

        let styles = [
            new ol_style.Style({
                fill: color ? new ol_style.Fill({ color: <any>color }) : null,

                stroke: selected || highlight ? selectedStroke.outer.getStroke() : defaultStroke,
            }),
        ];

        if (selected || highlight) {
            styles.push(selectedStroke.inner);
        }

        let showText = layer.showLabels || layer.showFieldNames;
        showText = showText && includeText;
        showText = showText && highlight !== false;
        showText = showText && (layer.showSelectedText !== false || !(selected || highlight));

        if (showText) {
            const texts = this.getText(feature, data, resolution);

            if (texts) {
                const textStyles = getTextStyles(resolution, texts);

                styles = styles.concat(textStyles);
            }
        }

        return styles;
    }

    protected isSelected(feature: ol_Feature, selected: boolean) {
        return selected;
    }

    protected getFillColorOpacity(selected: boolean) {
        const { layer } = this;

        let opacity = !selected || layer.fillSelectedStyle ? 1 : 0.1;
        opacity *= selected ? 0.3 : 0.5;

        return opacity;
    }

    protected getText(feature: ol_Feature, data: FieldViewModel, resolution: number) {
        let text = this.labelTextProvider ? this.labelTextProvider(data) : undefined;

        if (!text) {
            const layer = this.layer;

            let lines: string[] = [];

            if (shouldIgnoreCropInLabels(data.cropId)) {
                return null;
            }

            const includeArea = layer.showLabel("area");

            const addLine = (str: string, area?: number) => {
                lines.push(includeArea && !isUndefinedOrNull(area) ? `${str} (${hectareFormat.format(area)})` : str);
            };

            // For croppings with siblings, only show the field labels for the largest cropping in the field.
            // (Determined earlier, largest sibling has `ShowFieldNameKey` applied).
            if (feature.get(ShowFieldNameKey)) {
                if (layer.showFieldNames && data.fieldName) {
                    addLine(data.fieldName, data.field.BoundaryArea);
                }

                if (layer.showLabel("fieldNumber") && data.field.FieldNumber) {
                    addLine(data.field.FieldNumber, data.field.BoundaryArea);
                }
            }

            const cropLabel = layer.showLabels || layer.showCropNames;

            if (cropLabel && layer.checkResolutionLabelFilter(resolution, 10)) {
                if (layer.showCropNames && layer.showLabels) {
                    let cropText = data.getCropName();

                    if (data.yearId !== layer.currentYearId) {
                        cropText += " (" + data.yearId + ")";
                    }

                    addLine(cropText, data.cropping.Area);
                }

                if (layer.showVarietyNames && data.cropping.Variety) {
                    addLine(data.cropping.Variety.Name, data.cropping.Area);
                }

                if (layer.showLabel("croppingDescription") && data.cropping.CropDescr) {
                    addLine(data.cropping.CropDescr, data.cropping.Area);
                }

                if (layer.showLabel("plotId") && data.cropping.PlotId) {
                    addLine(data.cropping.PlotId, data.cropping.Area);
                }

                if (layer.showLabel("sfiManagementOptions")) {
                    const options = data.cropping.Management?.Options;

                    if (!isArrayEmpty(options)) {
                        for (const opt of options) {
                            addLine(opt.Code, opt.Area ?? data.cropping.Area);
                        }
                    }
                }

                text = lines.join("\n");
            }
        }

        return [
            {
                text,
                fontSize: 14,
            },
        ];
    }

    protected getCroppingColor(feature: ol_Feature, selected: boolean, opacity: number): FillColor {
        const cropping = <FieldViewModel>feature.get("data");

        const patternName = getCropPatternName(cropping?.cropId);

        if (patternName) {
            const pattern = loadImgPattern(patternName, () => this.layer.refresh());
            if (pattern) {
                return pattern;
            }
        }

        let color = this.fillColorProvider ? this.fillColorProvider(cropping) : undefined;

        if (!color) {
            const style = cropping.style[this.layer.croppingStyleMode] ?? cropping.style["crop"];

            color = style?.Color;
            const patternId = style?.Pattern;

            if (color && patternId) {
                color = getOrCreateCropStylePattern(color, patternId, opacity) || color;
            }
        }

        if (!color) {
            color = [255, 255, 255];
        }

        if (isArray(color) && color.length == 3 && opacity !== 1) {
            color = [...color, opacity];
        }

        return color;
    }
}
